import {Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject, takeUntil} from 'rxjs';
import {fuseAnimations} from '@fuse/animations';
import {FuseAlertType} from '@fuse/components/alert';
import {FuseValidators} from '@fuse/validators/validators';
import {PolizaService} from '@services/poliza.service';
import {DiscordService} from '@services/discord.service';
import {PolizaRequest, PolizaResponse} from '@models/poliza.interface';
import {GenericSelect} from '@models/generic-select.interface';
import {PolizaTipo} from '@enums/PolizaTipo';
import {gettext} from '@utils/util-builder';
import * as pdfjsLib from 'pdfjs-dist';

interface _DatosPolizaPdf {
    numeroPoliza:   string | null;
    aseguradora:    string | null;
    monto:          number | null;
    vigenciaInicio: Date   | null;
    vigenciaFin:    Date   | null;
}

@Component({
    selector: 'app-poliza-form',
    templateUrl: './poliza-form.component.html',
    styleUrls: ['./poliza-form.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PolizaFormComponent implements OnInit, OnDestroy {

    form: UntypedFormGroup;
    enviando: boolean = false;
    isExtractingPdf: boolean = false;
    mostrarAvisoExtraccionFallida: boolean = false;
    flashMessage: { type: FuseAlertType; message: string } | null = null;
    contratantesDisponibles: GenericSelect[] = [];
    esEdicion: boolean = false;
    esEdicionMaster: boolean = false;
    polizaId: number | null = null;

    private readonly _maxSizeBytes = 10 * 1024 * 1024;
    private readonly _destroy$: Subject<void> = new Subject<void>();

    private readonly _MESES_ES: Record<string, string> = {
        ENE: '01', FEB: '02', MAR: '03', ABR: '04', MAY: '05', JUN: '06',
        JUL: '07', AGO: '08', SEP: '09', OCT: '10', NOV: '11', DIC: '12'
    };

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly _polizaService: PolizaService,
        private readonly _discordService: DiscordService,
        private readonly _dialogRef: MatDialogRef<PolizaFormComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly _data: {
            modo?: string;
            poliza?: PolizaResponse;
            proveedorId?: number;
            proveedorNombre?: string;
        } | null
    ) {
        this.esEdicionMaster = this._data?.modo === 'edit-master';
        this.esEdicion = this._data?.modo === 'edit' || this.esEdicionMaster;
    }

    ngOnInit(): void {
        pdfjsLib.GlobalWorkerOptions.workerSrc =
            '//cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.worker.js';

        this.form = this._fb.group({
            numeroPoliza:   ['', Validators.required],
            aseguradora:    ['', Validators.required],
            monto:          ['', [Validators.required, Validators.min(0.01), Validators.max(9_999_999_999_999)]],
            vigenciaInicio: ['', Validators.required],
            vigenciaFin:    ['', [Validators.required, this._vigenciaFinFutura]],
            file:           [undefined, [Validators.required, FuseValidators.maxContentSize(this._maxSizeBytes)]],
            pagada:         [false],
            contratanteIds: [[], [Validators.required, this._minLengthArray(1)]]
        }, {validators: this._fechaFinMayorQueInicio});

        const proveedorIdParaContratantes: number | undefined = this.esEdicionMaster
            ? this._data?.poliza?.proveedorId
            : this._data?.proveedorId;
        this._polizaService.getContratantesDisponibles(proveedorIdParaContratantes).subscribe({
            next: (contratantes: GenericSelect[]): void => {
                this.contratantesDisponibles = contratantes;
            },
            error: (): void => {
                this.mostrarAvisoExtraccionFallida = false;
                this.flashMessage = {
                    type: 'error',
                    message: 'No fue posible cargar los contratantes disponibles. Cierra el diálogo e intenta de nuevo.'
                };
            }
        });

        if (this.esEdicion && this._data?.poliza) {
            this._prepararModoEdicion(this._data.poliza);
            return;
        }

        this.form.get('file')!.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((file: File | null) => {
                if (file && file.type === 'application/pdf') {
                    this._extraerDatosPdf(file);
                }
            });
    }

    private _prepararModoEdicion(poliza: PolizaResponse): void {
        this.polizaId = poliza.id;
        this.form.patchValue({
            numeroPoliza:   poliza.numeroPoliza,
            aseguradora:    poliza.aseguradora,
            monto:          poliza.monto,
            vigenciaInicio: this._isoToDate(poliza.vigenciaInicio),
            vigenciaFin:    this._isoToDate(poliza.vigenciaFin),
            pagada:         poliza.pagada,
            contratanteIds: (poliza.contratantes ?? []).map((c: GenericSelect): number => c.id)
        });

        // El archivo de reemplazo es opcional en ambos modos de edición (no se exige uno nuevo).
        this.form.get('file')!.clearValidators();
        this.form.get('file')!.updateValueAndValidity();

        if (!this.esEdicionMaster) {
            // Proveedor: solo "contratanteIds" es editable; el resto queda en modo consulta (deshabilitado/gris).
            ['numeroPoliza', 'aseguradora', 'monto', 'vigenciaInicio', 'vigenciaFin', 'pagada', 'file']
                .forEach((control: string): void => this.form.get(control)!.disable());
        }
        // MASTER (edit-master): ningún campo se deshabilita — puede editar cualquier dato (AC-4).
    }

    private _isoToDate(value: string): Date | null {
        if (!value) { return null; }
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        if (this.esEdicion) {
            if (this.esEdicionMaster) {
                this._guardarComoMaster();
            } else {
                this._guardarContratantes();
            }
            return;
        }

        const v = this.form.value;
        const archivoPdf: File = v.file;

        const polizaData: PolizaRequest = {
            numeroPoliza:   v.numeroPoliza,
            tipoPoliza:     PolizaTipo.RESPONSABILIDAD_CIVIL,
            aseguradora:    v.aseguradora,
            monto:          Number(v.monto),
            vigenciaInicio: this._formatDate(v.vigenciaInicio),
            vigenciaFin:    this._formatDate(v.vigenciaFin),
            pagada:         v.pagada,
            contratanteIds: v.contratanteIds,
            proveedorId:    this._data?.proveedorId
        };

        this.enviando = true;
        this.flashMessage = null;
        this._polizaService.createPoliza(polizaData, archivoPdf).subscribe({
            next: (poliza: PolizaResponse): void => {
                this.enviando = false;
                // Notificación a Discord como último paso del alta (tras validar → S3 → BD).
                // El proveedor puede seleccionar varios contratantes: enviar la lista completa separada por comas.
                const contratantes: string = (poliza.contratantes ?? [])
                    .map((c: GenericSelect): string => c.name)
                    .join(', ');
                const compania: string = contratantes || (poliza.companiaNombre ?? '');
                const esMaster: boolean = this._data?.proveedorId != null;
                this._discordService.sendPolizasFianzasChannelMessage({
                    username: esMaster ? 'ALTA POLIZA (MASTER)' : 'ALTA POLIZA',
                    content: esMaster
                        ? `MASTER cargó la póliza ${poliza.numeroPoliza} en representación del proveedor ${poliza.proveedorNombre} para la compañia ${compania}`
                        : `El proveedor ${poliza.proveedorNombre} ha cargado la póliza ${poliza.numeroPoliza} para la compañia ${compania}`
                });
                this._dialogRef.close(true);
            },
            error: (err: HttpErrorResponse): void => {
                this.enviando = false;
                this.flashMessage = {
                    type: 'error',
                    message: this._resolverMensajeError(err)
                };
            }
        });
    }

    private _guardarComoMaster(): void {
        this.enviando = true;
        this.flashMessage = null;
        const v = this.form.getRawValue();
        const archivoPdf: File | undefined = v.file;

        const polizaData: PolizaRequest = {
            numeroPoliza:   v.numeroPoliza,
            tipoPoliza:     PolizaTipo.RESPONSABILIDAD_CIVIL,
            aseguradora:    v.aseguradora,
            monto:          Number(v.monto),
            vigenciaInicio: this._formatDate(v.vigenciaInicio),
            vigenciaFin:    this._formatDate(v.vigenciaFin),
            pagada:         v.pagada,
            contratanteIds: v.contratanteIds
        };

        this._polizaService.updateAsMaster(this.polizaId!, polizaData, archivoPdf).subscribe({
            next: (poliza: PolizaResponse): void => {
                this.enviando = false;
                this._discordService.sendPolizasFianzasChannelMessage({
                    username: 'EDICIÓN POLIZA (MASTER)',
                    content: `MASTER editó la póliza ${poliza.numeroPoliza} en representación del proveedor ${poliza.proveedorNombre}`
                });
                this._dialogRef.close(true);
            },
            error: (err: HttpErrorResponse): void => {
                this.enviando = false;
                this.flashMessage = {
                    type: 'error',
                    message: this._resolverMensajeError(err)
                };
            }
        });
    }

    private _guardarContratantes(): void {
        this.enviando = true;
        this.flashMessage = null;
        const contratanteIds: number[] = this.form.get('contratanteIds')!.value;
        this._polizaService.updateContratantes(this.polizaId!, {contratanteIds}).subscribe({
            next: (): void => {
                this.enviando = false;
                this._dialogRef.close(true);
            },
            error: (err: HttpErrorResponse): void => {
                this.enviando = false;
                this.flashMessage = {
                    type: 'error',
                    message: this._resolverMensajeError(err)
                };
            }
        });
    }

    cancelar(): void {
        this._dialogRef.close(false);
    }

    private _minLengthArray(min: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            return Array.isArray(value) && value.length >= min ? null : {minLengthArray: true};
        };
    }

    // -----------------------------------------------------------------------
    // Extracción de PDF
    // -----------------------------------------------------------------------

    private _extraerDatosPdf(file: File): void {
        this.isExtractingPdf = true;
        this.mostrarAvisoExtraccionFallida = false;

        const reader = new FileReader();
        reader.onload = async (event): Promise<void> => {
            try {
                const texto: string = await gettext(event.target?.result);
                const datos: _DatosPolizaPdf = this._parsearTextoPdf(texto);
                this._rellenarFormulario(datos);
            } catch {
                this.mostrarAvisoExtraccionFallida = true;
            } finally {
                this.isExtractingPdf = false;
            }
        };
        reader.onerror = (): void => {
            this.isExtractingPdf = false;
            this.mostrarAvisoExtraccionFallida = true;
        };
        reader.readAsArrayBuffer(file);
    }

    private _parsearTextoPdf(texto: string): _DatosPolizaPdf {
        const t = texto.replace(/\s+/g, ' ');
        const datos: _DatosPolizaPdf = {
            numeroPoliza:   null,
            aseguradora:    null,
            monto:          null,
            vigenciaInicio: null,
            vigenciaFin:    null
        };

        // Número de póliza: "Póliza: QA 45000089" o "Expide la Póliza con número QA45000089"
        const polizaPatterns = [
            /P[ÓO]LIZA[:\s]+([A-Z]{2,3}\s*\d{4,})/i,
            /P[ÓO]LIZA\s+con\s+n[ÚU]mero\s+([A-Z]{2,3}\s*\d{4,})/i,
            /N[ÚU]MERO\s+DE\s+P[ÓO]LIZA[:\s]*([A-Z0-9\s\-]{4,20})/i
        ];
        for (const pattern of polizaPatterns) {
            const m = t.match(pattern);
            if (m?.[1]?.trim()) { datos.numeroPoliza = m[1].trim(); break; }
        }

        // Aseguradora: “Aseguradora o Compañía: Chubb Seguros México, S.A.”
        const aseguradoraMatch = t.match(
            /Aseguradora\s+o\s+Compa[ñn][ií]a:\s*(.*?(?:S\.A\.|S\.C\.|A\.C\.|S\.A\b))/i
        );
        if (aseguradoraMatch?.[1]?.trim()) {
            datos.aseguradora = aseguradoraMatch[1].trim();
        } else {
            // Fallback: nombre de aseguradora conocida en el texto
            const conocidas = ['Chubb', 'AXA', 'GNP', 'Mapfre', 'Zurich', 'HDI', 'Allianz', 'MetLife', 'SURA', 'Qualitas'];
            for (const nombre of conocidas) {
                if (new RegExp(nombre, 'i').test(t)) { datos.aseguradora = nombre; break; }
            }
        }

        // Monto / Suma asegurada
        // Patrón principal: "MXN $35,000,000 como límite único y combinado"
        // Patrón secundario: "$35,000,000.00" inmediato después de "RESPONSABILIDAD CIVIL GENERAL"
        const montoPatterns = [
            /MXN\s*\$\s*([\d,]+(?:\.\d{2})?)\s+como\s+l[ií]mite/i,
            /RESPONSABILIDAD\s+CIVIL\s+GENERAL\s*\$\s*([\d,]+(?:\.\d{2})?)/i,
            /L[IÍ]MITE\s+[ÚU]NICO\s+Y\s+COMBINADO[^$]*\$\s*([\d,]+(?:\.\d{2})?)/i,
            /SUMA\s+ASEGURADA[^$]*\$\s*([\d,]+(?:\.\d{2})?)/i
        ];
        for (const pattern of montoPatterns) {
            const m = t.match(pattern);
            if (m?.[1]) {
                const valor = parseFloat(m[1].replace(/,/g, ''));
                if (!isNaN(valor) && valor > 0) { datos.monto = valor; break; }
            }
        }

        // Vigencia — formato carátula: "Vigencia: Del 22/AGO/2025 12:00 horas al 22/AGO/2026"
        const fechaPatterns = [
            /Vigencia:\s*Del?\s+(\d{1,2}\/[A-ZÁÉÍÓÚÑ]{3}\/\d{4}).*?al\s+(\d{1,2}\/[A-ZÁÉÍÓÚÑ]{3}\/\d{4})/i,
            /Del?\s+(\d{1,2}\/[A-ZÁÉÍÓÚÑ]{3}\/\d{4}).*?al\s+(\d{1,2}\/[A-ZÁÉÍÓÚÑ]{3}\/\d{4})/i,
            /(\d{1,2}\s+de\s+\w+\s+de\s+\d{4})\s+al\s+(\d{1,2}\s+de\s+\w+\s+de\s+\d{4})/i,
            /INICIO[:\s]*(\d{1,2}[\/\-]\w{2,3}[\/\-]\d{4}).*?(?:FIN|VENCIMIENTO)[:\s]*(\d{1,2}[\/\-]\w{2,3}[\/\-]\d{4})/i
        ];
        for (const pattern of fechaPatterns) {
            const m = t.match(pattern);
            if (m?.[1] && m?.[2]) {
                datos.vigenciaInicio = this._parsearFecha(m[1]);
                datos.vigenciaFin    = this._parsearFecha(m[2]);
                if (datos.vigenciaInicio && datos.vigenciaFin) { break; }
            }
        }

        return datos;
    }

    private _parsearFecha(texto: string): Date | null {
        if (!texto) { return null; }

        // Formato largo: "22 de agosto de 2025"
        const mLargo = texto.match(/^(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})$/i);
        if (mLargo) {
            const mesNombre = mLargo[2].substring(0, 3).toUpperCase();
            const mesNum = this._MESES_ES[mesNombre];
            if (mesNum) {
                const d = new Date(parseInt(mLargo[3]), parseInt(mesNum) - 1, parseInt(mLargo[1]));
                return isNaN(d.getTime()) ? null : d;
            }
        }

        // Normalizar mes abreviado en español: "AGO" → "08"
        const normalizado = texto.replace(
            /([A-ZÁÉÍÓÚÑ]{3,})/gi,
            (mes) => this._MESES_ES[mes.substring(0, 3).toUpperCase()] ?? mes
        );

        // Intentar DD/MM/YYYY o DD-MM-YYYY
        const m = normalizado.match(/^(\d{1,2})[\/\-](\d{2})[\/\-](\d{4})$/);
        if (m) {
            const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
            return isNaN(d.getTime()) ? null : d;
        }

        // Intentar YYYY-MM-DD
        const m2 = normalizado.match(/^(\d{4})[\/\-](\d{2})[\/\-](\d{2})$/);
        if (m2) {
            const d = new Date(parseInt(m2[1]), parseInt(m2[2]) - 1, parseInt(m2[3]));
            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    }

    private _rellenarFormulario(datos: _DatosPolizaPdf): void {
        let camposLlenados = 0;

        if (datos.numeroPoliza) {
            this.form.get('numeroPoliza')!.setValue(datos.numeroPoliza);
            camposLlenados++;
        }
        if (datos.aseguradora) {
            this.form.get('aseguradora')!.setValue(datos.aseguradora);
            camposLlenados++;
        }
        if (datos.monto !== null) {
            this.form.get('monto')!.setValue(datos.monto);
            camposLlenados++;
        }
        if (datos.vigenciaInicio) {
            this.form.get('vigenciaInicio')!.setValue(datos.vigenciaInicio);
            camposLlenados++;
        }
        if (datos.vigenciaFin) {
            this.form.get('vigenciaFin')!.setValue(datos.vigenciaFin);
            camposLlenados++;
        }

        this.mostrarAvisoExtraccionFallida = camposLlenados === 0;
    }

    // -----------------------------------------------------------------------
    // Validadores y helpers
    // -----------------------------------------------------------------------

    private _vigenciaFinFutura(control: AbstractControl): ValidationErrors | null {
        if (!control.value) { return null; }
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        return new Date(control.value) < hoy ? {vigenciaVencida: true} : null;
    }

    private _fechaFinMayorQueInicio(group: UntypedFormGroup): ValidationErrors | null {
        const inicio = group.get('vigenciaInicio')?.value;
        const fin = group.get('vigenciaFin')?.value;
        if (inicio && fin && new Date(fin) <= new Date(inicio)) {
            return {fechaRangeInvalid: true};
        }
        return null;
    }

    private _resolverMensajeError(err: HttpErrorResponse): string {
        const mensajeBackend: string = err?.error?.error?.error;
        if (err.status === 400 && mensajeBackend) {
            return 'La fecha de fin de vigencia debe ser una fecha futura.';
        }
        if (err.status === 409) {
            return mensajeBackend ?? 'Ya existe una póliza activa con ese número. Verifica el número de póliza.';
        }
        if (err.status === 404) {
            return 'El proveedor indicado no es válido. Verifica que sea un proveedor existente.';
        }
        return 'Ocurrió un error al cargar la póliza. Intenta de nuevo.';
    }

    private _formatDate(value: string | Date): string {
        if (!value) { return ''; }
        const d = new Date(value);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${d.getFullYear()}-${month}-${day}`;
    }
}
